import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  callDiagnosisService,
  type DiagnosisFeedbackRow,
  type DiagnosisVerdict,
} from "@/services/callDiagnosisService";

interface Props {
  messageId: string;
  feedback: DiagnosisFeedbackRow[];
  currentUserId: string | null;
  onChange: (rows: DiagnosisFeedbackRow[]) => void;
}

export function DiagnosisFeedback({ messageId, feedback, currentUserId, onChange }: Props) {
  const mine = feedback.find((f) => f.user_id === currentUserId);
  const others = feedback.filter((f) => f.user_id !== currentUserId);
  const [note, setNote] = useState(mine?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNote(mine?.note ?? "");
  }, [mine?.note]);

  const apply = (row: DiagnosisFeedbackRow | null) => {
    const rest = feedback.filter((f) => f.user_id !== currentUserId);
    onChange(row ? [...rest, row] : rest);
  };

  const save = async (verdict: DiagnosisVerdict, noteText: string) => {
    setSaving(true);
    setError(null);
    try {
      apply(await callDiagnosisService.upsertFeedback(messageId, verdict, noteText));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save feedback");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (verdict: DiagnosisVerdict) => {
    if (mine?.verdict === verdict) {
      setSaving(true);
      setError(null);
      try {
        await callDiagnosisService.deleteFeedback(messageId);
        apply(null);
        setNote("");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not remove feedback");
      } finally {
        setSaving(false);
      }
      return;
    }
    await save(verdict, note);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Was this right?</span>
        <Button
          type="button"
          size="sm"
          variant={mine?.verdict === "correct" ? "default" : "outline"}
          className="h-7 px-2 text-xs"
          disabled={saving}
          onClick={() => toggle("correct")}
        >
          <Check className="mr-1 h-3 w-3" />
          Correct
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mine?.verdict === "wrong" ? "destructive" : "outline"}
          className="h-7 px-2 text-xs"
          disabled={saving}
          onClick={() => toggle("wrong")}
        >
          <X className="mr-1 h-3 w-3" />
          Wrong
        </Button>
      </div>

      {mine && (
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note.trim() !== (mine.note ?? "")) save(mine.verdict, note);
          }}
          placeholder="Optional note: what was right or wrong?"
          rows={2}
          className="min-h-0 text-xs"
        />
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      {others.length > 0 && (
        <ul className="space-y-0.5">
          {others.map((f) => (
            <li key={f.id} className="text-xs text-muted-foreground">
              <span className={f.verdict === "correct" ? "text-good" : "text-danger"}>
                {f.verdict === "correct" ? "✓" : "✗"}
              </span>{" "}
              {f.user_email ?? "someone"}
              {f.note ? `: ${f.note}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
